import type { BlockStyle } from '@/blocks/domain/types';
import { buildPriceBookBySheetView, slicePriceBookBySheetView } from '@/document-render/domain/priceBook/buildPriceBookBySheetView';
import type { SliceInfo } from '@/document-render/domain/RendererContract';
import { useDocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';
import { PriceBookSheetGroup } from './PriceBookSheetGroup';
import { measuredFontSizeCss } from '@/document-render/application/blockFontSize';
import { EMPTY_STYLE, PRICE_BOOK_EMPTY_TEXT, TITLE_STYLE, priceBookSectionStyle } from './priceBookSheetStyles';
import { PriceBookQuoteSheets } from './quote/PriceBookQuoteSheets';
import { usePriceBookCanvas } from './quote/priceBookEditing';

export interface PriceBookBySheetProps {
  rows: readonly unknown[];
  currency: string;
  cultureTag: string;
  title?: string;
  style: BlockStyle;
  slice?: SliceInfo;
}

/**
 * Bound price book grouped by rate sheet. Read-only (no chrome, events untouched) until the host
 * wires a price-book callback; then the quote canvas takes over the same view model.
 */
export function PriceBookBySheet({ rows, currency, cultureTag, title, style, slice }: PriceBookBySheetProps) {
  const audience = useDocumentAudience();
  const canvas = usePriceBookCanvas();
  const view = buildPriceBookBySheetView(rows, { audience, currency, cultureTag });
  const sheets = slicePriceBookBySheetView(view, slice);
  const isFirstSlice = !slice || slice.startIndex === 0;

  return (
    <section aria-label="block-price-book" style={priceBookSectionStyle(style)}>
      {title && isFirstSlice ? <div style={TITLE_STYLE}>{title}</div> : null}
      {view.rowCount === 0 && isFirstSlice ? <div style={EMPTY_STYLE}>{PRICE_BOOK_EMPTY_TEXT}</div> : null}
      {canvas ? (
        <PriceBookQuoteSheets canvas={canvas} allSheets={view.sheets} sheets={sheets} />
      ) : (
        sheets.map((sheet) => <PriceBookSheetGroup key={sheet.sheetId} sheet={sheet} headerFontSize={measuredFontSizeCss(style)} />)
      )}
    </section>
  );
}
